import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";

const RANGE = "standings_am!A1:AI100"; // wir zeigen A..AD an, brauchen aber AG..AI für die Bild-URLs

const colIndex = (letter) => {
    let n = 0;
    for (let i = 0; i < letter.length; i++) n = n * 26 + (letter.charCodeAt(i) - 64);
    return n;
};

const LETTER_Y  = colIndex("Y");   // neues Ende der sichtbaren Tabelle (war AD)
const LETTER_K  = colIndex("K");   // neuer Start der Rennspalten (war P)
const LETTER_V  = colIndex("V");   // neuer letzter Rennlauf (war AA)

const isNumeric = (v) => v !== null && v !== "" && !isNaN(Number(v));

function computeTop3ByColumn(rowsAsArrays, startCol, endCol) {
    const map = new Map();
    for (let c = startCol; c <= endCol; c++) {
        const nums = rowsAsArrays
            .map(arr => arr[c - 1])
            .filter(v => v !== null && v !== undefined && v !== "")
            .map(v => Number(v))
            .filter(v => !Number.isNaN(v));
        if (!nums.length) { map.set(c, null); continue; }

        const uniqDesc = Array.from(new Set(nums)).sort((a, b) => b - a);
        map.set(c, {
            gold:   uniqDesc[0] ?? null,
            silver: uniqDesc[1] ?? null,
            bronze: uniqDesc[2] ?? null,
        });
    }
    return map;
}

export default function StandingsPro() {
    const [data, setData] = useState({ header: [], rows: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchSheetViaProxy({ range: RANGE });
                setData(res);
            } catch (e) {
                const msg = e?.message || String(e);
                // Sonderfall: Range existiert noch nicht -> freundliche Empty-Message
                if (msg.includes("Unable to parse range")) {
                    setNoData(true);
                } else {
                    setError(msg);
                }
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { visibleHeaders, indices, rowsAsArrays, top3PerRaceColumn, completedRuns } = useMemo(() => {
        const headers = data.header || [];
        const visibleHeaders = headers.slice(0, LETTER_Y);

        const idxAB = colIndex("AB") - 1; // Flag URL (war AG)
        const idxAC = colIndex("AC") - 1; // Car  URL (war AH)
        const idxAD = colIndex("AD") - 1; // Team URL (war AI)

        const rowsAsArrays = (data.rows || []).map((r) => headers.map((h) => r[h]));
        const top3PerRaceColumn = computeTop3ByColumn(rowsAsArrays, LETTER_K, LETTER_V);

        // === NEU: gefahrene Läufe zählen (eine Spalte zählt als "gefahren",
        // wenn irgendeine Zeile dort eine Zahl hat)
        let completedRuns = 0;
        for (let c = LETTER_K; c <= LETTER_V; c++) {
            const anyNumberInCol = rowsAsArrays.some(row => {
                const v = row[c - 1];
                return v !== null && v !== "" && !Number.isNaN(Number(v));
            });
            if (anyNumberInCol) completedRuns++;
        }

        return {
            visibleHeaders,
            indices: { idxAB, idxAC, idxAD },
            rowsAsArrays,
            top3PerRaceColumn,
            completedRuns, // <<— zurückgeben
        };
    }, [data]);

    if (loading) return <div className="card"><p>Lade Standings...</p></div>;
    if (noData) {
        return (
            <div className="card">
                <h2>Standings <span className="pill pill--am">AM</span></h2>
                <p className="muted">
                    Aktuell sind noch keine Rennergebnisse im System.
                    Sobald das erste Rennen gefahren ist, erscheinen hier die AM-Standings der GT3 Xperience.
                </p>
            </div>
        );
    }
    if (error)   return <div className="card"><p style={{ color:"#f55" }}>Fehler: {error}</p></div>;

    return (
        <>
            <div className="card">
                <h2>Standings <span className="pill pill--am">AM</span></h2>
                <p className="muted">
                    AM Wertung nach Abschluss des {completedRuns}. Laufes präsentiert von BavarianSimTec.
                </p>
            </div>

            <div className="fancyTable">
                <table className="tbl">
                    <thead>
                    <tr>
                        {visibleHeaders.map((h, i) => {

                            const colNumber = i + 1;

                            // Kopfzeilen-Anpassungen:
                            // D = Fahrzeugnummer -> "#"
                            const label =
                                colNumber === 1 ? "Pos" :
                                    colNumber === 4 ? "#"   :
                                        h;

                            const isRace = colNumber >= LETTER_K && colNumber <= LETTER_V;
                            const raceLeft = colNumber === LETTER_K;
                            const raceRight = colNumber === LETTER_V;
                            const evenStripe = isRace && ((colNumber - LETTER_K) % 2 === 0);

                            // Linksbündig nur A (Pos) und F (Fahrername)
                            const leftAligned = (colNumber === 1 || colNumber === 6);

                            const thClass = [
                                isRace ? "race-col" : "",
                                raceLeft ? "race-left" : "",
                                raceRight ? "race-right" : "",
                                evenStripe ? "race-alt" : "",
                                // Bildspalten: C (Flag), E (Car), G (Teamlogo)
                                (colNumber === 3 || colNumber === 5 || colNumber === 7) ? "icon" : "",
                                leftAligned ? "align-left" : "",
                            ].filter(Boolean).join(" ");

                            return <th key={i} className={thClass}>{label}</th>;
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {rowsAsArrays.map((arr, ri) => (
                        <tr key={ri}>
                            {visibleHeaders.map((_, i) => {
                                const colNumber = i + 1;
                                const val = arr[i];

                                // Bilder laut deiner Spezifikation:
                                // C: Flag (aus AG), E: Car (aus AH), G: Teamlogo (aus AI)
                                if (colNumber === 3) {
                                    const src = arr[indices.idxAB] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Flag" className="flag lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }
                                if (colNumber === 5) {
                                    const src = arr[indices.idxAC] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Car" className="carlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }
                                if (colNumber === 7) {
                                    const src = arr[indices.idxAD] || "";
                                    return (
                                        <td key={i} className="icon">
                                            {src ? <img src={src} alt="Team" className="teamlogo lg" loading="lazy" /> : null}
                                        </td>
                                    );
                                }

                                // Rennspalten P..AA mit Top-3-Highlight
                                const isRace = colNumber >= LETTER_K && colNumber <= LETTER_V;
                                const raceLeft = colNumber === LETTER_K;
                                const raceRight = colNumber === LETTER_V;
                                const evenStripe = isRace && ((colNumber - LETTER_K) % 2 === 0);

                                let tierClass = "";
                                if (isRace && val !== "" && val !== null && val !== undefined) {
                                    const numVal = Number(val);
                                    const t3 = top3PerRaceColumn.get(colNumber);
                                    if (t3 && !Number.isNaN(numVal)) {
                                        if (numVal === t3.gold)        tierClass = "race-gold";
                                        else if (numVal === t3.silver) tierClass = "race-silver";
                                        else if (numVal === t3.bronze) tierClass = "race-bronze";
                                    }
                                }

                                // Linksbündig nur A (Pos) und F (Fahrername)
                                const leftAligned = (colNumber === 1 || colNumber === 6 || colNumber === 8);

                                // Delta Pos (B) einfärben
                                let deltaClass = "";
                                if (colNumber === 2) {
                                    const n = Number(val);
                                    if (!isNaN(n)) {
                                        if (n > 0) deltaClass = "delta-up";
                                        else if (n < 0) deltaClass = "delta-down";
                                    }
                                }

                                const compactMetrics = ""; // metrics wurden entfernt
                                const numeric = isNumeric(val) || isRace || (colNumber >= 1 && colNumber <= 9);

                                // ✅ DNS / DNF / BAN Status (nur Rennspalten!)
                                let statusClass = "";
                                if (isRace) {
                                    const s = String(val ?? "").trim().toUpperCase();
                                    if (s === "DNS") statusClass = "status-dns";
                                    else if (s === "DNF") statusClass = "status-dnf";
                                    else if (s === "BAN") statusClass = "status-ban";
                                }

                                const tdClass = [
                                    numeric ? "num" : "",
                                    isRace ? "race-col" : "",
                                    raceLeft ? "race-left" : "",
                                    raceRight ? "race-right" : "",
                                    evenStripe ? "race-alt" : "",
                                    tierClass,
                                    leftAligned ? "align-left" : "",
                                    (colNumber === 9) ? "points-highlight" : "", // Points (I)
                                    (colNumber === 1) ? "pos-highlight" : "",    // Pos (A)
                                    deltaClass,
                                    compactMetrics,
                                    statusClass, // ✅ NEU
                                ].filter(Boolean).join(" ");

                                return <td key={i} className={tdClass}>{val}</td>;
                            })}
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}
